import { useState, useEffect } from 'react';
import PageMeta from '../../shared/PageMeta';
import PageBreadcrumb from '../../shared/PageBreadCrumb';
import Chart from 'react-apexcharts';
import {
  Users,
  LogIn,
  FolderPlus,
  FileSpreadsheet,
  RefreshCw,
  Activity,
  Circle,
  Clock,
  TrendingUp,
  Zap,
  User,
} from 'lucide-react';
import {
  dashboardService,
  AdminManagerHeadActivity,
  AdminActivityItem,
  ManagerHeadUserSummary,
} from '../../services/dashboardService';
import { toast } from 'react-hot-toast';

const OFFLINE_DOT = 'bg-slate-300';

function ActivityIcon({ type }: { type: string }) {
  switch (type) {
    case 'login':
      return <LogIn className="h-3.5 w-3.5 text-blue-500" />;
    case 'project_created':
      return <FolderPlus className="h-3.5 w-3.5 text-violet-500" />;
    case 'report_generated':
      return <FileSpreadsheet className="h-3.5 w-3.5 text-amber-500" />;
    default:
      return <Activity className="h-3.5 w-3.5 text-slate-400" />;
  }
}

function StatMini({
  label,
  value,
  sub,
  icon: Icon,
  animate = true,
  delay = 0,
}: {
  label: string;
  value: number;
  sub?: string;
  icon: React.ElementType;
  animate?: boolean;
  delay?: number;
}) {
  const [display, setDisplay] = useState(0);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  useEffect(() => {
    if (!mounted) return;
    if (!animate) {
      setDisplay(value);
      return;
    }
    const step = Math.max(1, value / 15);
    let v = 0;
    const id = setInterval(() => {
      v += step;
      if (v >= value) {
        setDisplay(value);
        clearInterval(id);
      } else {
        setDisplay(Math.floor(v));
      }
    }, 20);
    return () => clearInterval(id);
  }, [value, animate, mounted]);

  return (
    <div
      className="group flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200/70 bg-white/80 py-2 pl-2 pr-2 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-[#339274]/40 hover:shadow-md hover:bg-white"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600 transition group-hover:bg-[#339274]/10 group-hover:text-[#339274]">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{label}</p>
        <p className="text-lg font-bold tabular-nums leading-tight text-slate-800">{display}</p>
        {sub != null && <p className="text-[10px] text-slate-400">{sub}</p>}
      </div>
    </div>
  );
}

export default function AdminActivityDashboard() {
  const [data, setData] = useState<AdminManagerHeadActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      else setRefreshing(true);
      setError(null);
      const res = await dashboardService.getAdminManagerHeadActivity({ limit: 80 });
      setData(res);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load activity';
      setError(msg);
      if (showLoading) toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const t = setInterval(() => load(false), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (loading && !data) {
    return (
      <>
        <PageMeta title="Manager & Head Activity - Admin" description="Track manager and head user activity" />
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#339274] border-t-transparent" />
            <p className="text-[11px] text-slate-500">Loading activity...</p>
          </div>
        </div>
      </>
    );
  }

  if (error && !data) {
    return (
      <>
        <PageMeta title="Manager & Head Activity - Admin" description="Track manager and head user activity" />
        <div className="rounded-lg border border-red-200 bg-red-50/80 p-3 text-red-800">
          <p className="text-xs font-medium">Error loading dashboard</p>
          <p className="text-[11px]">{error}</p>
        </div>
      </>
    );
  }

  const d = data!;
  const { counts, activityByDay, recentActivity, userSummaries } = d;

  const areaOptions = {
    chart: {
      type: 'area' as const,
      height: 160,
      fontFamily: 'Inter, sans-serif',
      toolbar: { show: false },
      animations: { enabled: true, speed: 500, dynamicAnimation: { enabled: true } },
      stacked: false,
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth' as const, width: 1.5 },
    fill: { type: 'gradient', opacity: 0.35 },
    colors: ['#3B82F6', '#8B5CF6', '#F59E0B'],
    xaxis: {
      categories: activityByDay.map((x) => {
        const dt = new Date(x.date);
        return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      }),
      labels: { style: { colors: '#64748B', fontSize: '9px' } },
    },
    yaxis: { labels: { style: { colors: '#64748B', fontSize: '9px' } } },
    legend: {
      position: 'top' as const,
      horizontalAlign: 'right' as const,
      fontSize: '9px',
      labels: { colors: '#64748B' },
    },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 2,
      xaxis: { lines: { show: false } },
    },
    tooltip: { theme: 'light', x: { format: 'dd MMM' } },
  };

  const areaSeries = [
    { name: 'Logins', data: activityByDay.map((x) => x.logins) },
    { name: 'Projects', data: activityByDay.map((x) => x.projectsCreated) },
    { name: 'Reports', data: activityByDay.map((x) => x.reportsGenerated) },
  ];

  const barOptions = {
    chart: {
      type: 'bar' as const,
      height: 160,
      fontFamily: 'Inter, sans-serif',
      toolbar: { show: false },
      animations: { enabled: true, speed: 500 },
    },
    plotOptions: {
      bar: { horizontal: false, columnWidth: '75%', borderRadius: 3 },
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 1, colors: ['transparent'] },
    colors: ['#339274'],
    xaxis: {
      categories: activityByDay.map((x) => {
        const dt = new Date(x.date);
        return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      }),
      labels: { style: { colors: '#64748B', fontSize: '9px' } },
    },
    yaxis: { labels: { style: { colors: '#64748B', fontSize: '9px' } } },
    legend: { show: false },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 2,
      xaxis: { lines: { show: false } },
    },
    tooltip: { theme: 'light' },
  };

  const totalActivity = activityByDay.map((x) => x.logins + x.projectsCreated + x.reportsGenerated);
  const barSeries = [{ name: 'Total', data: totalActivity }];

  return (
    <>
      <PageMeta title="Manager & Head Activity - Admin" description="Track manager and head user activity" />
      <div className="space-y-3">
        {/* Header: compact, live badge, refresh */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <PageBreadcrumb pageTitle="Manager & Head Activity" />
            <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
              LIVE
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400">
              {d.lastUpdated ? new Date(d.lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--'}
            </span>
            <button
              type="button"
              onClick={() => {
                load(false);
                toast.success('Refreshed');
              }}
              disabled={refreshing}
              className="flex items-center gap-1.5 rounded-md bg-[#339274] px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm transition hover:bg-[#28765e] disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* KPI strip: 8 mini cards, single row, small */}
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
          {[
            { label: 'M/H users', value: counts.totalManagerHead, icon: Users, sub: undefined },
            { label: 'Online', value: counts.onlineNow, icon: Circle, sub: '15m' },
            { label: 'Logins today', value: counts.loginsToday, icon: LogIn, sub: undefined },
            { label: 'Logins 7d', value: counts.loginsThisWeek, icon: TrendingUp, sub: undefined },
            { label: 'Projects today', value: counts.projectsCreatedToday, icon: FolderPlus, sub: undefined },
            { label: 'Projects 7d', value: counts.projectsCreatedThisWeek, icon: FolderPlus, sub: undefined },
            { label: 'Reports today', value: counts.reportsGeneratedToday, icon: FileSpreadsheet, sub: undefined },
            { label: 'Reports 7d', value: counts.reportsGeneratedThisWeek, icon: FileSpreadsheet, sub: undefined },
          ].map((s, i) => (
            <StatMini key={s.label} label={s.label} value={s.value} sub={s.sub} icon={s.icon} delay={i * 30} />
          ))}
        </div>

        {/* Charts: compact height, same row */}
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200/70 bg-white/80 p-2.5 shadow-sm backdrop-blur-sm transition hover:shadow-md">
            <div className="mb-1.5 flex items-center justify-between">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Activity over time</h3>
              <Zap className="h-3.5 w-3.5 text-amber-500" />
            </div>
            <Chart options={areaOptions} series={areaSeries} type="area" height={160} />
          </div>
          <div className="rounded-lg border border-slate-200/70 bg-white/80 p-2.5 shadow-sm backdrop-blur-sm transition hover:shadow-md">
            <div className="mb-1.5 flex items-center justify-between">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total activity (14d)</h3>
            </div>
            <Chart options={barOptions} series={barSeries} type="bar" height={160} />
          </div>
        </div>

        {/* Bottom: Activity feed (dense) + User list (dense) */}
        <div className="grid gap-3 lg:grid-cols-3">
          {/* Activity feed: small rows, more lines */}
          <div className="lg:col-span-2 overflow-hidden rounded-lg border border-slate-200/70 bg-white/80 shadow-sm backdrop-blur-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-2.5 py-1.5">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Recent activity (all important)</h3>
              <span className="text-[9px] text-slate-400">{recentActivity.length} events</span>
            </div>
            <div className="max-h-[380px] overflow-y-auto">
              {recentActivity.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                  <Activity className="mb-1.5 h-6 w-6" />
                  <p className="text-[11px]">No recent activity</p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100/80">
                  {recentActivity.map((item: AdminActivityItem, idx: number) => (
                    <li
                      key={item.id}
                      className="flex items-center gap-2 px-2.5 py-1.5 transition hover:bg-slate-50/80"
                      style={{ animationDelay: `${idx * 20}ms` }}
                    >
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-100">
                        <ActivityIcon type={item.type} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-medium leading-tight text-slate-800">{item.message}</p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[9px] text-slate-500">
                          <span className="font-medium text-slate-500">{item.userName}</span>
                          <span className="text-slate-300">·</span>
                          <span className="text-slate-400">{item.userRole}</span>
                          <span className="text-slate-300">·</span>
                          <span className="flex items-center gap-0.5 text-slate-400">
                            <Clock className="h-2.5 w-2.5" />
                            {item.relativeTime}
                          </span>
                          {item.metadata?.projectName && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span className="truncate max-w-[120px] text-slate-400" title={item.metadata.projectName}>
                                {item.metadata.projectName}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* User list: compact table-like */}
          <div className="overflow-hidden rounded-lg border border-slate-200/70 bg-white/80 shadow-sm backdrop-blur-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-2.5 py-1.5">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Manager & Head users</h3>
              <span className="text-[9px] text-slate-400">{userSummaries.length} users</span>
            </div>
            <div className="max-h-[380px] overflow-y-auto">
              {userSummaries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                  <User className="mb-1.5 h-6 w-6" />
                  <p className="text-[11px]">No users</p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100/80">
                  {userSummaries.map((u: ManagerHeadUserSummary) => (
                    <li
                      key={u.userId}
                      className="flex items-center gap-2 px-2.5 py-1.5 transition hover:bg-slate-50/80"
                    >
                      <div
                        className={`h-2 w-2 shrink-0 rounded-full ${u.isOnline ? 'animate-pulse bg-emerald-500' : OFFLINE_DOT}`}
                        title={u.isOnline ? 'Online' : 'Offline'}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[11px] font-medium text-slate-800">{u.name}</p>
                        <p className="truncate text-[9px] text-slate-500">{u.roleName}</p>
                        <p className="mt-0.5 text-[9px] text-slate-400">
                          {u.lastLoginAt
                            ? `${new Date(u.lastLoginAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })} ${new Date(u.lastLoginAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                            : 'Never'}
                        </p>
                      </div>
                      <div className="shrink-0 text-right text-[9px] tabular-nums text-slate-500">
                        <div>P:{u.projectsCreatedCount}</div>
                        <div>R:{u.reportsGeneratedCount}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
